import AdminButton from 'components/button';
import Button from 'components/crm/ui/Button';
import DateField from 'components/crm/ui/DateField';
import { FIELD_GAP_CLASS, LABEL_CLASS, SELECT_CLASS } from 'components/crm/ui/fieldStyles';
import Label from 'components/crm/ui/Label';
import RichSelect from 'components/crm/ui/RichSelect';
import Switch from 'components/crm/ui/Switch';
import TextField from 'components/crm/ui/TextField';
import RecurrenceField from 'components/planning/fields/RecurrenceField';
import RemindersField from 'components/planning/fields/RemindersField';
import ScopeDialog from 'components/planning/ScopeDialog';
import WeekPicker from 'components/planning/WeekPicker';
import TaskLinksField from 'components/tasks/form/TaskLinksField';
import { attendeesOf, colleagueLink, linkOfAttendee, lockedLinksOf, recordLinkOf } from 'components/planning/withWhom';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import useWideSlideOver from 'components/SlideOver/wideSlideOver';
import { EVENT_CATEGORIES, eventCategoryText } from 'constants/eventCategories';
import { apiErrorText } from 'helper/apiErrorText';
import { sameRule } from 'helper/recurrence';
import { addDays, clockLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import { SUPER_ADMIN_ROLE } from 'helper/roles';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useText from 'hooks/useText';
import { CalendarClock, MapPin, Phone, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { contactRefField, contactRefOf } from 'server/crm/crmRecords';

const pad = (value) => String(value).padStart(2, '0');
export const clockOf = (date) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// The local moment of `day` (YYYY-MM-DD) at `clock` (HH:MM), or null.
export const momentOf = (day, clock) => {
  const [year, month, date] = (day ?? '').split('-').map(Number);
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock ?? '');
  if (!year || !month || !date || !match) return null;
  return new Date(year, month - 1, date, Number(match[1]), Number(match[2]));
};

// The local day `days` after `day` (both YYYY-MM-DD).
export const shiftDay = (day, days) => isoDay(addDays(fromIsoDay(day), days));

const daysBetween = (from, to) =>
  from && to ? Math.round((fromIsoDay(to) - fromIsoDay(from)) / 86400000) : 0;

const DEFAULT_REMINDERS = { useDefault: true, overrides: [] };

// How a type of rendez-vous is held, told by its icon: on the phone (no place
// asked), between colleagues, or on site.
const CATEGORY_WAYS = {
  telephone: { icon: Phone, description: 'Par téléphone, sans lieu' },
  interne: { icon: Users, description: 'Entre collègues' },
};
const ON_SITE = { icon: MapPin, description: 'Sur place' };
// The types a rendez-vous takes with whom it is held: without a company,
// between colleagues or on the phone; with a prospect (or a company lost), no
// client visit nor hiring; with a client, no prospecting visit. « RDV client »
// is no longer offered: it was the client visit under another name.
const CATEGORIES_BY_STAGE = {
  none: ['interne', 'telephone'],
  prospect: ['prospection', 'presentation', 'telephone'],
  client: ['visite_client', 'embauche', 'presentation', 'telephone'],
};
// « Avec qui ? »: its kinds, in this order (components/planning/withWhom).
const WITH_WHOM_TYPES = ['company', 'contact', 'colleague', 'email'];
const OFFERED_CATEGORIES = Object.keys(EVENT_CATEGORIES).filter((category) => category !== 'rdv_client');
const stageKeyOf = (companyId, stage) => {
  if (!companyId) return 'none';
  return stage === 'prospect' || stage === 'lost' ? 'prospect' : 'client';
};
// The type kept if it still fits, else the visit of the stage (the first).
const fittingCategory = (category, allowed) => (allowed.includes(category) ? category : allowed[0]);
// How an appointment of a type is held, as its icon tells (the panel shows it too).
export const categoryWayOf = (category) => CATEGORY_WAYS[category] ?? ON_SITE;
const categoryOption = (language, category) => ({
  value: category,
  label: eventCategoryText(language, category, 'label'),
  iconClass: 'text-gray-500',
  ...categoryWayOf(category),
});

const fullName = (person) =>
  [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// The slot of an appointment: its days are shown with the last one included,
// the api's endDay of a whole day being the day after.
const slotOf = (item) => {
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  if (item.allDay) {
    const startDay = item.startDay ?? isoDay(start);
    return {
      allDay: true,
      startDay,
      endDay: item.endDay ? shiftDay(item.endDay, -1) : startDay,
      start: '09:00',
      end: '10:00',
    };
  }
  return {
    allDay: false,
    startDay: isoDay(start),
    endDay: isoDay(end),
    start: clockOf(start),
    end: clockOf(end),
  };
};

// An appointment « à placer » has no slot yet (« Pas encore de date »): it
// is placed by giving it one (retours du 31/10/2026).
const TO_PLACE_STATUS = 'to_place';
const NO_SLOT = { allDay: false, startDay: '', endDay: '', start: '09:00', end: '10:00' };

// The form of an appointment to edit; `initial` of a new one is already in
// the form's shape (the draft of the quick creation). A new one of a record
// is a client appointment with its contact; one of the planning, internal.
const formOf = (mode, item, initial, record) =>
  mode === 'create'
    ? {
        toPlace: false,
        category: initial?.category ?? (record ? 'visite_client' : 'interne'),
        // « Avec qui ? » once touched; null until then, drawn from `record`.
        withWhom: null,
        title: initial?.title ?? '',
        ownerId: initial?.ownerId ?? null,
        allDay: Boolean(initial?.allDay),
        startDay: initial?.startDay ?? '',
        endDay: initial?.endDay ?? initial?.startDay ?? '',
        start: initial?.start ?? '09:00',
        end: initial?.end ?? '10:00',
        recurrence: initial?.recurrence ?? null,
        meet: false,
        location: '',
        // The company's address put in « Lieu »: replaced while nobody types another place.
        prefilledLocation: '',
        reminders: DEFAULT_REMINDERS,
        description: initial?.description ?? '',
      }
    : {
        toPlace: item.status === TO_PLACE_STATUS,
        title: item.title ?? '',
        ownerId: item.ownerId ?? null,
        ...(item.startsAt ? slotOf(item) : NO_SLOT),
        withWhom: [...lockedLinksOf(item), ...(item.attendees ?? []).map(linkOfAttendee)],
        // An appointment with a Meet link already: the box stays ticked.
        meet: Boolean(item.meet) || item.conference?.kind === 'meet',
        location: item.location ?? '',
        reminders: item.reminders ?? DEFAULT_REMINDERS,
        description: item.description ?? '',
        recurrence: item.series?.rule ?? null,
      };

const SelectField = ({ id, label, value, onChange, options }) => (
  <div className={`flex flex-col ${FIELD_GAP_CLASS}`}>
    <Label htmlFor={id}>{label}</Label>
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={SELECT_CLASS}
    >
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </select>
  </div>
);

/**
 * The whole form of an appointment, in the order of Google Calendar: a new
 * one (`mode` 'create', from « Plus d'options » of the quick creation,
 * `initial` its draft) or « Modifier » of one (`mode` 'edit', `item` its
 * detail). The Meet link and the reminders only exist in a calendar synced
 * with Google (`calendarSynced`). Saves through createAppointment or
 * updateAppointment and hands the appointment returned to `onSaved`;
 * « Annuler » (and Échap while editing) calls `onCancel`. A title is required
 * only without a company, as on the api. A new one may repeat (« Répéter »,
 * `initial.recurrence` from the quick creation): its rule goes as
 * `recurrence`, none for « Ne se répète pas ». An occurrence of a series
 * (`item.recurring`) shows the rule of its series, editable unless Google
 * wrote it outside of our choices or it was not read yet; saving asks for its
 * scope first (ScopeDialog) and sends it with the fields, the rule only when
 * it changed.
 *
 * A new one may be of a record (`record`: { companyId, name, contacts,
 * contactName }): it is linked to its company and chooses its type.
 * « Avec qui ? » holds whom it is held with, as many as needed: colleagues,
 * contacts, any address typed, each invited (`attendees`), and with
 * `chooseRecord` the companies too: the company ticked, else that of the
 * first person, is the one it is linked to, and its first person there the
 * contact met (`contactName`, `contactRef`, JBWEB-1427; from a page, the
 * page's `contactRef` for its own contact); editing one, its company and
 * contact met stay as they are, shown fixed. A new one of a group (`groupId`)
 * is linked to it as well, beside its company if any. `layout` 'panel'
 * fills a slide-over as in HubSpot: the week it falls in on the left (WeekPicker, the slide-over widened), `header`, the
 * fields under `prepend` and the buttons in the panel's footer on the right.
 */
const AppointmentForm = ({
  mode = 'edit',
  item,
  initial,
  record = null,
  contactRef = null,
  groupId = null,
  chooseRecord = false,
  calendarSynced = false,
  layout = 'inline',
  header = null,
  prepend = null,
  onSaved,
  onCancel,
}) => {
  const creating = mode === 'create';
  const panel = layout === 'panel';
  useWideSlideOver(panel);
  const { text, language } = useText('appointment');
  const { text: planningText } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { text: fieldsText } = useText('appointmentFields');
  const { text: recurrenceText } = useText('recurrence');
  const { call, me } = useCrmContext();
  // Only the agency manager (500) puts a rendez-vous in someone else's calendar.
  const { userData } = Usefetchdata();
  const manager = userData?.role === SUPER_ADMIN_ROLE;
  const [form, setForm] = useState(() => formOf(mode, item, initial, record));
  // An appointment always holds in one day, between two hours. Only one
  // already over a whole day or several (from Google Calendar) keeps its days.
  const [wholeOrLongSlot] = useState(
    () => form.allDay || form.startDay !== form.endDay,
  );
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  // In a panel, the week read beside the form tells whether Google is synced.
  const [weekSynced, setWeekSynced] = useState(false);
  // The rendez-vous of the week read beside the form: one the slot overlaps is told.
  const [weekEvents, setWeekEvents] = useState([]);
  const synced = calendarSynced || weekSynced;
  useEffect(() => {
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
    call(crmApi.getContacts).then(setContacts, () => setContacts([]));
  }, [call]);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // Bubbling, after the calendar of the day field: an Échap that closes the
  // calendar (default prevented) leaves the form open. A new appointment sits
  // in a Drawer, which handles Échap itself.
  useEffect(() => {
    if (creating) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented)
        cancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [creating]);

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));
  const set = (field) => (event) => update({ [field]: event.target.value });
  // The span is kept when the first day moves, as in Google Calendar.
  const setStartDay = (startDay) =>
    setForm((current) => ({
      ...current,
      startDay,
      endDay: shiftDay(startDay, daysBetween(current.startDay, current.endDay)),
    }));

  const colleagues = team.filter((person) => person.id !== me?.id);
  const ownerId = creating
    ? manager && form.ownerId && form.ownerId !== me?.id
      ? form.ownerId
      : null
    : item.ownerId;
  const owner = colleagues.find((person) => person.id === ownerId) ?? null;
  const calendarOwnerId = ownerId ?? me?.id;
  const guests = team.filter((person) => person.id !== calendarOwnerId);

  const startsAt = momentOf(form.startDay, form.start);
  const endsAt = momentOf(form.endDay, form.end);
  const timeKnown = form.allDay
    ? Boolean(form.startDay && form.endDay)
    : Boolean(startsAt && endsAt);
  const timeOk = form.allDay
    ? timeKnown && form.endDay >= form.startDay
    : timeKnown && endsAt > startsAt;
  const timeError =
    timeKnown && !timeOk ? fieldsText('invalidTime') : null;
  // `chooseRecord`: the company is chosen in the form, given (`record`) or
  // not; else it is the record's own.
  const choosing = creating && chooseRecord;
  // A rendez-vous « Téléphonique » has no place to ask for; an « Interne »
  // one is held between colleagues only: no company, no contact.
  const category = creating ? form.category : item.category;
  const byPhone = category === 'telephone';
  const internal = category === 'interne';
  const companyOptions = contacts.filter((option) => option.companyId);
  const optionOfCompany = (id) =>
    id ? companyOptions.find((option) => option.companyId === id && !option.contact) ?? null : null;
  // « Avec qui ? », as « Associer à » of a task: as many people as needed,
  // colleagues, contacts or any address typed, and (chosen here) companies.
  // Untouched, it holds the record's contact, else (chosen here) its company.
  const recordContact = record?.contactName
    ? companyOptions.find((option) => option.contact === record.contactName && option.companyId === record.companyId) ?? null
    : null;
  const seedLinks = () => {
    if (recordContact) return [recordLinkOf(recordContact)];
    if (!choosing || !record?.companyId) return [];
    const option = optionOfCompany(record.companyId);
    return [option ? recordLinkOf(option) : { type: 'company', id: record.companyId, label: record.name || '', optionKey: null }];
  };
  const withWhom = form.withWhom ?? seedLinks();
  // The company it is held with: the one ticked, else that of the first person.
  const chosenCompanyId =
    withWhom.find((link) => link.type === 'company')?.id ??
    withWhom.find((link) => link.type === 'contact' && link.companyId)?.companyId ??
    null;
  const typesCompanyId = choosing ? chosenCompanyId : record?.companyId ?? null;
  const companyId = creating ? (choosing && internal ? null : typesCompanyId) : null;
  const companyOption = optionOfCompany(typesCompanyId);
  // « Lieu » takes the address of the company met (admin/crm/contacts), once known.
  const companyAddress = creating && !internal ? companyOption?.address ?? '' : '';
  useEffect(() => {
    setForm((current) =>
      current.location === current.prefilledLocation
        ? { ...current, location: companyAddress, prefilledLocation: companyAddress }
        : current,
    );
  }, [companyAddress]);
  // The types that fit the company it is held with; anything else: all. The
  // type moves to one that fits (a prospect gets no client visit).
  const stageKey = creating && (choosing || typesCompanyId) ? stageKeyOf(typesCompanyId, companyOption?.stage) : null;
  const allowedCategories = stageKey ? CATEGORIES_BY_STAGE[stageKey] : OFFERED_CATEGORIES;
  useEffect(() => {
    if (!stageKey) return;
    setForm((current) => ({ ...current, category: fittingCategory(current.category, CATEGORIES_BY_STAGE[stageKey]) }));
  }, [stageKey]);
  // The person met: the first one of that company. Untouched without a match
  // among the options, the record's contact as the page gave it.
  const metLink = withWhom.find((link) => link.type === 'contact' && link.companyId === typesCompanyId) ?? null;
  const metOption = metLink?.optionKey ? contacts.find((option) => option.key === metLink.optionKey) ?? null : null;
  const recordContactRef = () =>
    contactRef ?? contactRefOf(record?.contacts?.find((contact) => contact.name === record?.contactName));
  const contactName = metLink?.label ?? (form.withWhom ? '' : initial?.contactName ?? record?.contactName ?? '');
  const metContactRef = metLink
    ? !choosing && metLink.label === record?.contactName
      ? recordContactRef()
      : metOption?.contactRef ?? contactRefOf(metOption)
    : form.withWhom || choosing
      ? null
      : recordContactRef();
  // Colleagues always. Companies only when chosen here, where one moves the
  // type off « Interne »; elsewhere, between colleagues, nobody else. A guest
  // added to one already made is invited, so needs an address.
  const colleaguesOnly = internal && !choosing;
  const withWhomOptions = [
    ...guests.map(colleagueLink),
    ...(colleaguesOnly
      ? []
      : companyOptions
          .filter((option) => (option.contact ? creating || option.email : choosing))
          .map(recordLinkOf)),
  ];
  const titleOk =
    Boolean(form.title.trim()) ||
    (creating ? Boolean(companyId) : Boolean(item.companyId || item.company));
  // « Pas encore de date »: offered on a new one (without a repetition), and
  // on one still to place, which a date then places.
  const mayWaitForDate = creating ? !initial?.recurrence : item.status === TO_PLACE_STATUS;
  const canSave = (form.toPlace || timeOk) && titleOk && !busy;
  const recurring = !creating && Boolean(item.recurring);
  const ruleEditable = recurring && Boolean(item.series?.rule);
  const ruleChanged =
    ruleEditable && !sameRule(form.recurrence, item.series.rule);
  // Fixed while editing: a rule chosen among the presets must not follow a
  // move of this one occurrence to another day.
  const [ruleDay] = useState(() => (creating || !item.startsAt ? null : slotOf(item).startDay));

  // A new one in one's own calendar, at a time: the first rendez-vous of the
  // week it overlaps (a whole day or a canceled one does not count).
  const overlap =
    creating && !ownerId && !form.toPlace && !form.allDay && startsAt && endsAt && endsAt > startsAt
      ? weekEvents.find(
          (event) =>
            !event.allDay &&
            event.status !== 'canceled' &&
            new Date(event.startsAt) < endsAt &&
            new Date(event.endsAt) > startsAt,
        )
      : null;
  const overlapTitle = overlap
    ? overlap.masked
      ? 'Occupé'
      : (overlap.category === 'interne' ? overlap.title : overlap.company?.name || overlap.title) || 'Sans titre'
    : '';

  const save = async (scope) => {
    if (!canSave) return;
    if (recurring && !scope) {
      setScopeOpen(true);
      return;
    }
    setScopeOpen(false);
    setBusy(true);
    setError('');
    // Without a date: no slot, no Meet nor repetition until it is placed.
    const slot = form.toPlace
      ? { toPlace: true }
      : form.allDay
        ? {
            allDay: true,
            startDay: form.startDay,
            endDay: shiftDay(form.endDay, 1),
          }
        : {
            allDay: false,
            startsAt: startsAt.toISOString(),
            endsAt: endsAt.toISOString(),
          };
    const fields = {
      title: form.title.trim(),
      // By phone, no place: a new one sends none, an edited one keeps its own.
      location: byPhone ? (creating ? '' : (item.location ?? '')) : form.location.trim(),
      ...slot,
      description: form.description.trim(),
      // Between colleagues: a contact invited before the type was set leaves.
      attendees: attendeesOf(withWhom, { ownerId: calendarOwnerId, colleaguesOnly: internal }),
      ...(form.toPlace ? {} : { meet: form.meet }),
      reminders: form.reminders,
    };
    try {
      if (creating) {
        const created = await call(crmApi.createAppointment, {
          ...(ownerId ? { ownerId } : {}),
          ...fields,
          ...(form.recurrence && !form.toPlace ? { recurrence: form.recurrence } : {}),
          category: form.category,
          ...(companyId ? { companyId } : {}),
          ...(groupId ? { groupId } : {}),
          ...(contactName && !(choosing && internal) ? { contactName } : {}),
          ...contactRefField(choosing && internal ? null : metContactRef),
        });
        toast.success(
          owner
            ? planningText('meetingAddedFor', { name: fullName(owner) })
            : planningText('meetingAdded'),
        );
        onSaved(created);
      } else {
        const updated = await call(crmApi.updateAppointment, {
          id: item.id,
          fields: ruleChanged
            ? { ...fields, recurrence: form.recurrence }
            : fields,
          ...(scope ? { scope } : {}),
        });
        toast.success(text('updated'));
        onSaved(updated);
      }
    } catch (failure) {
      const message = apiErrorText(failure, { language, text: tasksText, group: 'errors' });
      if (message) setError(message);
      setBusy(false);
    }
  };

  // A slot picked in the week: its day and start, its length kept on a click.
  const pickSlot = ({ start, minutes }) =>
    setForm((current) => {
      const from = momentOf(current.startDay, current.start);
      const to = momentOf(current.endDay, current.end);
      const length = minutes ?? (from && to && to > from ? (to - from) / 60000 : 60);
      return {
        ...current,
        toPlace: false,
        allDay: false,
        startDay: isoDay(start),
        endDay: isoDay(new Date(start.getTime() + length * 60000)),
        start: clockOf(start),
        end: clockOf(new Date(start.getTime() + length * 60000)),
      };
    });

  // In a panel, spaced as the task's form it switches with (Tâche ↔ Rendez-vous).
  const categoryField = (
    <RichSelect
      id="appointment-form-category"
      label={fieldsText('categoryLabel')}
      value={form.category}
      onChange={(category) => update({ category })}
      options={allowedCategories.map((category) => categoryOption(language, category))}
    />
  );
  const titleField = (
    <TextField
      id="appointment-form-title"
      label={planningText('titleLabel')}
      // The invitation sent to the guests carries it.
      helper="Visible par les personnes invitées"
      value={form.title}
      maxLength={200}
      onChange={set('title')}
    />
  );
  const fields = (
    <div className={`flex flex-col ${panel ? 'gap-5' : 'gap-4'}`}>
      {prepend}
      {/* A card of the form (white, grey border); only its icon warns, in orange. */}
      {overlap && (
        <div role="status" className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-yellow-50 text-yellow-500">
            <CalendarClock size={16} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-5 text-gray-900">Créneau déjà occupé</p>
            <p className="mt-0.5 text-sm leading-5 text-gray-600">
              {`Vous avez déjà « ${overlapTitle} » de ${clockLabel(new Date(overlap.startsAt))} à ${clockLabel(
                new Date(overlap.endsAt),
              )}.`}
            </p>
          </div>
        </div>
      )}
      {/* Its title, whom (« Avec qui ? »), then (chosen here) its type among
          those that fit; from a page, the type first. */}
      {creating && !choosing && categoryField}
      {titleField}
      <TaskLinksField
        label="Avec qui ?"
        placeholder={
          colleaguesOnly
            ? 'Rechercher un collègue'
            : choosing
              ? 'Rechercher une entreprise, une personne ou une adresse e-mail'
              : 'Rechercher une personne ou une adresse e-mail'
        }
        openUpwards={false}
        withTempWorkers={false}
        types={WITH_WHOM_TYPES}
        allowEmails={!internal}
        options={withWhomOptions}
        value={withWhom}
        onChange={(links) => update({ withWhom: links })}
      />
      {choosing && categoryField}
      {creating && manager && (colleagues.length > 0 || ownerId) && (
        <SelectField
          id="appointment-form-owner"
          label={planningText('calendarOf')}
          value={ownerId ?? ''}
          onChange={(value) =>
            setForm((current) => ({
              ...current,
              ownerId: value || null,
              // The new owner cannot stay a guest of their own appointment.
              withWhom: (current.withWhom ?? withWhom).filter(
                (link) => !(link.type === 'colleague' && link.id === (value || me?.id)),
              ),
            }))
          }
          options={[
            ['', fullName(me)],
            ...(ownerId && !owner ? [[ownerId, '']] : []),
            ...colleagues.map((person) => [person.id, fullName(person)]),
          ]}
        />
      )}
      {mayWaitForDate && (
        <Switch
          checked={form.toPlace}
          onChange={(toPlace) => update({ toPlace })}
          label="Pas encore de date (rendez-vous à placer)"
        />
      )}
      {form.toPlace ? null : wholeOrLongSlot ? (
        <>
        <Switch
          checked={form.allDay}
          onChange={(allDay) => update({ allDay })}
          label={text('allDayLabel')}
        />
        <div className="grid grid-cols-2 gap-3">
          <DateField
            id="appointment-form-start-day"
            label={text('startDayLabel')}
            value={form.startDay}
            onChange={setStartDay}
          />
          {!form.allDay && (
            <TextField
              id="appointment-form-start"
              type="time"
              label={text('startLabel')}
              value={form.start}
              onChange={set('start')}
            />
          )}
          <div className="flex flex-col gap-2">
            <DateField
              id="appointment-form-end-day"
              label={text('endDayLabel')}
              value={form.endDay}
              minDate={fromIsoDay(form.startDay || isoDay(new Date()))}
              onChange={(endDay) => update({ endDay })}
            />
            {form.allDay && timeError && (
              <p className="text-sm text-danger">{timeError}</p>
            )}
          </div>
          {!form.allDay && (
            <TextField
              id="appointment-form-end"
              type="time"
              label={text('endLabel')}
              value={form.end}
              onChange={set('end')}
              error={timeError}
            />
          )}
        </div>
        </>
      ) : (
        <>
          <DateField
            id="appointment-form-day"
            label="Jour"
            value={form.startDay}
            onChange={(day) => update({ startDay: day, endDay: day })}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="appointment-form-start"
              type="time"
              label={text('startLabel')}
              value={form.start}
              onChange={set('start')}
            />
            <TextField
              id="appointment-form-end"
              type="time"
              label={text('endLabel')}
              value={form.end}
              onChange={set('end')}
              error={timeError}
            />
          </div>
        </>
      )}
      {/* No « Répéter » on a new one, unless the quick creation brought a rule:
          shown then, so that nothing is sent unseen. */}
      {creating && Boolean(initial?.recurrence) && !form.toPlace && (
        <RecurrenceField
          value={form.recurrence}
          startDay={form.startDay}
          onChange={(recurrence) => update({ recurrence })}
        />
      )}
      {ruleEditable && (
        <RecurrenceField
          value={form.recurrence}
          startDay={ruleDay}
          onChange={(recurrence) => update({ recurrence })}
        />
      )}
      {recurring && !ruleEditable && (
        <div className="flex flex-col gap-2">
          <span className={LABEL_CLASS}>
            {recurrenceText('label')}
          </span>
          <p className="text-sm font-medium text-ink">
            {recurrenceText(item.series ? 'googleCustom' : 'repeats')}
          </p>
        </div>
      )}
      {synced && !form.toPlace && (
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium leading-5 text-ink">
          <input
            type="checkbox"
            className="checkbox-primary"
            checked={form.meet}
            onChange={(event) => update({ meet: event.target.checked })}
          />
          {fieldsText('meetLabel')}
        </label>
      )}
      {!byPhone && (
        <TextField
          id="appointment-form-location"
          label={text('locationLabel')}
          value={form.location}
          maxLength={200}
          onChange={set('location')}
        />
      )}
      {synced && (
        <RemindersField
          value={form.reminders}
          onChange={(reminders) => update({ reminders })}
        />
      )}
      <TextField
        id="appointment-form-description"
        multiline
        rows={4}
        label={planningText('descriptionLabel')}
        value={form.description}
        maxLength={5000}
        onChange={set('description')}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
  const scopeDialog = (
    <ScopeDialog
      open={scopeOpen}
      action="edit"
      ruleChanged={ruleChanged}
      onCancel={() => setScopeOpen(false)}
      onConfirm={save}
    />
  );

  if (!panel) {
    return (
      <div className="flex flex-col gap-4">
        {fields}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {text('cancelEdit')}
          </Button>
          <Button disabled={!canSave} onClick={() => save()}>
            {text('saveEdit')}
          </Button>
        </div>
        {scopeDialog}
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1">
      <div className="hidden min-w-0 flex-1 flex-col border-r border-gray-100 lg:flex">
        <WeekPicker
          start={!form.allDay && !form.toPlace ? startsAt : null}
          minutes={startsAt && endsAt && endsAt > startsAt ? (endsAt - startsAt) / 60000 : 60}
          title={form.title.trim() || (choosing ? companyOption?.company : record?.name) || ''}
          onPick={pickSlot}
          onLoaded={(week) => {
            setWeekSynced(Boolean(week?.calendarSynced));
            setWeekEvents(week?.events ?? []);
          }}
        />
      </div>
      {/* As wide as a task's form (NARROW_SLIDE_OVER_WIDTH): the week comes beside it. */}
      <div className="flex min-h-0 w-full flex-col lg:w-[600px] lg:flex-none">
        {header}
        <SlideOverBody>{fields}</SlideOverBody>
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <AdminButton color="SECONDARY" size="SM" onClick={onCancel}>
              {text('cancelEdit')}
            </AdminButton>
            <AdminButton color={canSave ? 'PRIMARY' : 'DISABLED'} size="SM" disabled={!canSave} onClick={() => save()}>
              {creating ? fieldsText('createCta') : text('saveEdit')}
            </AdminButton>
          </SlideOverFooter>
        </div>
      </div>
      {scopeDialog}
    </div>
  );
};

export default AppointmentForm;
